// src/context/CartContext.tsx
import React, { createContext, useState, useEffect, useMemo, useCallback } from "react";
import type { Product, Variant } from "@src/types/product";
import type { CartItem, CartContextType } from "@src/types/cart";

export const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = "cart";

const skuOf = (v?: Variant | null) => v?.sku ?? "base";
// If you know SKUs are globally unique, you can set cartLineId = variantSku.
// Using composite avoids accidental collisions:
const makeCartLineId = (productId: string, variantSku: string) => `${productId}__${variantSku}`;

// --- Migration: old shape { product, quantity, cartLineId } -> new shape
const migrateOld = (raw: any[]): CartItem[] => {
    if (!Array.isArray(raw)) return [];
    return raw
        .map((line) => {
            if (!line || typeof line !== "object") return null;

            // Already new shape?
            if (
                line.productId &&
                line.variantSku &&
                line.cartLineId &&
                typeof line.lockedPrice === "number"
            ) {
                return line as CartItem;
            }

            // Old shape
            const p: Product | undefined = line.product;
            if (!p?.id) return null;

            const v: Variant | undefined = (p as any)?.selectedVariant ?? undefined;
            const variantSku = skuOf(v);
            const cartLineId = makeCartLineId(p.id, variantSku);

            const priceFromVariant = v?.discountPrice || v!.price || 0;

            const lockedCompareAt =
                typeof v?.compareAt === "number" && v!.compareAt! > priceFromVariant
                    ? (v!.compareAt as number)
                    : undefined;

            const q = Number(line.quantity ?? 1);

            const migrated: CartItem = {
                productId: p.id,
                variantSku,
                cartLineId,
                quantity: q > 0 ? q : 1,
                lockedPrice: priceFromVariant,
                lockedCompareAt,
            };
            return migrated;
        })
        .filter(Boolean) as CartItem[];
};

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
    const [isRehydrated, setIsRehydrated] = useState(false);
    const [cart, setCart] = useState<CartItem[]>([]);

    // Rehydrate + migrate
    useEffect(() => {
        try {
            if (typeof window !== "undefined") {
                const saved = localStorage.getItem(CART_STORAGE_KEY);
                if (saved) {
                    const parsed = JSON.parse(saved);
                    setCart(migrateOld(parsed));
                }
            }
        } catch (error) {
            console.error("Error rehydrating cart:", error);
        } finally {
            setIsRehydrated(true);
        }
    }, []);

    // Persist
    useEffect(() => {
        if (typeof window !== "undefined" && isRehydrated) {
            localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
        }
    }, [cart, isRehydrated]);

    const totalItems = useMemo(
        () => cart.reduce((total, item) => total + item.quantity, 0),
        [cart],
    );

    // ✅ totals now use lockedPrice (not item.product.selectedVariant)
    const totalPrice = useMemo(
        () => cart.reduce((total, item) => total + item.lockedPrice * item.quantity, 0),
        [cart],
    );

    // --- Add to cart (SKU-first)
    const addToCart = useCallback((product: Product, quantity: number) => {
        setCart((prev) => {
            const selectedVariant = (product as any)?.selectedVariant as Variant | undefined;
            const variantSku = skuOf(selectedVariant);
            const cartLineId = makeCartLineId(product.id, variantSku);

            const priceFromVariant = selectedVariant?.discountPrice || selectedVariant!.price || 0;
            console.log(
                "priceFromVariant",
                priceFromVariant,
                "selectedVariant",
                selectedVariant,
                "product",
                product,
            );

            const lockedCompareAt =
                typeof selectedVariant?.compareAt === "number" &&
                (selectedVariant!.compareAt as number) > priceFromVariant
                    ? (selectedVariant!.compareAt as number)
                    : undefined;

            const idx = prev.findIndex((i) => i.cartLineId === cartLineId);
            if (idx > -1) {
                const copy = [...prev];
                copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + quantity };
                return copy;
            }
            const nextLine: CartItem = {
                productId: product.id,
                variantSku,
                cartLineId,
                quantity,
                lockedPrice: priceFromVariant,
                lockedCompareAt,
            };
            return [...prev, nextLine];
        });
    }, []);

    const removeLine = useCallback((cartLineId: string) => {
        setCart((prev) => prev.filter((i) => i.cartLineId !== cartLineId));
    }, []);

    // Back-compat: remove by productId (+ optional variantSku)
    const removeFromCart = useCallback((productId: string, variantSku?: string) => {
        setCart((prev) =>
            prev.filter((i) =>
                i.productId !== productId ? true : variantSku ? i.variantSku !== variantSku : false,
            ),
        );
    }, []);

    const updateLineQuantity = useCallback((cartLineId: string, quantity: number) => {
        setCart((prev) =>
            prev
                .map((i) => (i.cartLineId === cartLineId ? { ...i, quantity } : i))
                .filter((i) => i.quantity > 0),
        );
    }, []);

    // Back-compat: update by productId (+ optional variantSku)
    const updateQuantity = useCallback(
        (productId: string, quantity: number, variantSku?: string) => {
            setCart((prev) =>
                prev
                    .map((i) => {
                        const match =
                            i.productId === productId &&
                            (!variantSku || i.variantSku === variantSku);
                        return match ? { ...i, quantity } : i;
                    })
                    .filter((i) => i.quantity > 0),
            );
        },
        [],
    );

    const clearCart = useCallback(() => setCart([]), []);

    const value = useMemo(
        () => ({
            isRehydrated,
            cart,
            addToCart,
            removeLine,
            removeFromCart,
            updateLineQuantity,
            updateQuantity,
            clearCart,
            totalItems,
            totalPrice,
        }),
        [
            isRehydrated,
            cart,
            addToCart,
            removeLine,
            removeFromCart,
            updateLineQuantity,
            updateQuantity,
            clearCart,
            totalItems,
            totalPrice,
        ],
    );

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
